import { BillingPlanSummary } from '@/components/billing-plan-summary';
import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/hooks/use-tenant';
import { useGetClientPlansQuery, useGetClientStatsQuery } from '@/state';
import { Button } from '@/components/ui/button';

const parseCurrency = (rawCurrency?: string) => {
  if (!rawCurrency) return null;
  try {
    return JSON.parse(rawCurrency) as {
      code?: string;
      symbol?: string;
      name?: string;
    };
  } catch {
    return null;
  }
};

const formatPrice = (cost: string | undefined, rawCurrency?: string) => {
  const amount = Number.parseFloat(cost ?? '0');
  if (!Number.isFinite(amount)) return '0.00';

  const parsedCurrency = parseCurrency(rawCurrency);
  const currencyCode = parsedCurrency?.code;
  if (!currencyCode) return amount.toFixed(2);

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    const symbol = parsedCurrency?.symbol ?? '';
    return `${symbol}${amount.toFixed(2)}`;
  }
};

const formatDate = (isoDate?: string) => {
  if (!isoDate) return '';
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

const ActivePlan = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { data: plans = [], isLoading, isError } = useGetClientPlansQuery(
    tenant ? { tenant } : skipToken
  );
  const { data: clientStats } = useGetClientStatsQuery(
    tenant ? { tenant } : skipToken
  );

  const activePlan = useMemo(
    () => plans.find((planItem) => planItem.latest) ?? plans[0],
    [plans]
  );

  const modules = activePlan?.plan?.plan_modules_cost ?? [];
  const totalMonthly = modules.reduce((total, moduleItem) => {
    const amount = Number.parseFloat(moduleItem.cost ?? '0');
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);
  const maximumUsers = activePlan?.plan?.maximum_users ?? 0;
  const currentUsers = clientStats?.number_of_users ?? 0;
  const usagePercentage =
    maximumUsers > 0 ? Math.min((currentUsers / maximumUsers) * 100, 100) : 0;
  const planName =
    activePlan?.plan?.plan_name ?? activePlan?.plan_name ?? 'Unknown plan';
  const memberSince = formatDate(
    activePlan?.created_at ?? activePlan?.plan?.created_at
  );
  const features = (activePlan?.features ?? activePlan?.plan?.features ?? [])
    .map((item) => (typeof item === 'string' ? item : null))
    .filter((item): item is string => !!item);
  const moduleSummaries = modules.map((moduleItem) => ({
    id: moduleItem.id ?? `${moduleItem.module_code}-${moduleItem.module}`,
    name: moduleItem.module_name ?? moduleItem.module_code,
    priceLabel: `${formatPrice(moduleItem.cost, moduleItem.currency)} / month`,
  }));
  const totalLabel = `${formatPrice(totalMonthly.toString(), modules[0]?.currency)} / month`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isError || !activePlan) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          className="mb-4 gap-2 pl-0 hover:bg-transparent hover:text-primary"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="text-sm text-muted-foreground">
          Active plan data is not available.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-4xl">
      <Button
        variant="ghost"
        className="gap-2 pl-0 hover:bg-transparent hover:text-primary"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <BillingPlanSummary
        planName={planName}
        memberSince={memberSince}
        currentUsers={currentUsers}
        maximumUsers={maximumUsers}
        usagePercentage={usagePercentage}
        modules={moduleSummaries}
        totalLabel={totalLabel}
        features={features}
      />
    </div>
  );
};

export default ActivePlan;
